import type { ThemeMode } from '../contexts/ThemeContext';

/**
 * Pure reconciliation logic for syncing the local theme preference
 * (ThemeContext → AsyncStorage) with the server-backed
 * `UserPreferences.themeMode` field. Kept standalone from the bridge
 * component so the "which way does this write flow, and does it loop?"
 * decision is unit-testable without rendering the provider tree.
 *
 * ThemeContext stays the source of truth for first paint — it hydrates
 * from a plain AsyncStorage key before tRPC mounts, which is what
 * prevents the launch-time theme flash (ADR-0033). This reducer runs
 * once both the local value and the server record are available and
 * decides the single action to take.
 */

/** Carried across effect runs to dedupe both sync directions. */
export interface ThemeSyncState {
  /** True once we've performed the initial server↔local reconciliation
   *  for the current authenticated session. Reset on sign-out. */
  reconciled: boolean;
  /** The themeMode last known to agree with the server (received from
   *  it or just pushed to it). `undefined` until the first reconcile. */
  lastSyncedMode: ThemeMode | undefined;
}

export interface ThemeSyncStep {
  /** Next state to persist into the bridge's ref. */
  state: ThemeSyncState;
  /** When set, call ThemeContext.setMode with this (adopt server value). */
  applyLocal?: ThemeMode;
  /** When set, push this up via userPrefs.update (migrate / save toggle). */
  pushServer?: ThemeMode;
}

export const INITIAL_THEME_SYNC_STATE: ThemeSyncState = {
  reconciled: false,
  lastSyncedMode: undefined,
};

/**
 * Compute the next sync step.
 *
 * First reconcile (per session):
 *   - Server has a value → server wins. Adopt it locally (unless already
 *     equal) and record it as the synced value.
 *   - Server has no value → migrate the local choice up so an existing
 *     local preference isn't silently dropped on first sign-in.
 *
 * Ongoing:
 *   - A local mode that differs from the last-synced value is a user
 *     toggle → push it up. Recording `lastSyncedMode` on every push (and
 *     on the initial server-wins adopt) is what stops the inbound apply
 *     from echoing back out and a round-tripped write from looping.
 */
export function stepThemeSync(
  prev: ThemeSyncState,
  serverMode: ThemeMode | undefined,
  localMode: ThemeMode,
): ThemeSyncStep {
  if (!prev.reconciled) {
    if (serverMode) {
      // Server wins on load. Record it as synced so the setMode-driven
      // re-render below sees localMode === lastSyncedMode and no-ops
      // instead of pushing the freshly-adopted value straight back.
      return {
        state: { reconciled: true, lastSyncedMode: serverMode },
        ...(serverMode !== localMode ? { applyLocal: serverMode } : {}),
      };
    }
    // No server value yet — migrate the local choice up.
    return {
      state: { reconciled: true, lastSyncedMode: localMode },
      pushServer: localMode,
    };
  }

  if (localMode === prev.lastSyncedMode) {
    return { state: prev };
  }
  return {
    state: { reconciled: true, lastSyncedMode: localMode },
    pushServer: localMode,
  };
}
